<template>
  <v-dialog
    v-model="dialogOpen"
    :max-width="maxWidth"
    :persistent="isLocked"
    :scrollable="scrollable"
    role="dialog"
    :aria-labelledby="`${testid}-title`"
    :aria-describedby="`${testid}-body`"
    :data-testid="`${testid}-dialog`"
  >
    <v-card>
      <v-card-title class="app-dialog-title">
        <slot name="prepend" />
        <span :id="`${testid}-title`" class="app-dialog-title-text text-title-large">
          <slot name="title">{{ title }}</slot>
        </span>
        <v-spacer />
        <slot name="append" />
        <v-btn
          v-if="showClose"
          :icon="mdiClose"
          variant="text"
          density="comfortable"
          :disabled="busy"
          :aria-label="closeLabel"
          :data-testid="`${testid}-close`"
          @click="onClose"
        />
      </v-card-title>

      <v-card-text :id="`${testid}-body`">
        <slot />
      </v-card-text>

      <v-card-actions v-if="$slots.actions" :data-testid="`${testid}-actions`">
        <slot name="actions" />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { mdiClose } from "@mdi/js";
import { computed } from "vue";

// AppDialog is an app-owned contract, not a Vuetify passthrough (spec 4.6 rule
// 3). Disabling attribute inheritance stops undeclared attrs (e.g. fullscreen,
// transition) from falling through to the root v-dialog; approved options are
// exposed as explicit props instead.
defineOptions({ inheritAttrs: false });

/**
 * Standard dialog/card shell (see [FEPRIM-APP-DIALOG]). It owns the common modal shell, title association, close
 * behavior, action layout, and stable test ids, but never a business action.
 *
 * Consumers supply the title (prop or `title` slot), body (default slot), and
 * `actions` slot; `ConfirmDialog` composes this shell for confirmations.
 *
 * Generated test ids derive from `testid`: the dialog is `${testid}-dialog`,
 * the title `${testid}-title`, the body `${testid}-body`, the actions row
 * `${testid}-actions`, and the optional close affordance `${testid}-close`. The title and body ids are associated
 * with the dialog via `aria-labelledby` / `aria-describedby`.
 */
const props = withDefaults(
  defineProps<{
    modelValue: boolean;
    title: string;
    maxWidth?: number | string;
    /** Prevent overlay/Escape closure regardless of in-flight work. */
    persistent?: boolean;
    /** In-flight work: prevents accidental overlay/Escape/close-button closure. */
    busy?: boolean;
    scrollable?: boolean;
    /** Render a header close affordance that emits `close`. */
    showClose?: boolean;
    closeLabel?: string;
    testid: string;
  }>(),
  {
    maxWidth: 560,
    persistent: false,
    busy: false,
    scrollable: false,
    showClose: false,
    closeLabel: "Close",
  },
);

const emit = defineEmits<{
  "update:modelValue": [value: boolean];
  close: [];
}>();

const dialogOpen = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit("update:modelValue", value),
});

// `busy` shares the persistent lock so an in-flight action cannot be dismissed
// by an outside click or Escape.
const isLocked = computed(() => props.persistent || props.busy);

function onClose() {
  dialogOpen.value = false;
  emit("close");
}
</script>

<style scoped>
.app-dialog-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.app-dialog-title-text {
  min-width: 0;
}
</style>
